// AnalyzerPage - Image analysis and element regeneration
import { useState, useEffect, useRef } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import {
  Upload, Image as ImageIcon, Loader2, ChevronRight,
  Layers, User, Type, Play,
  ImagePlus, X, Check, History, Save,
  BookOpen, Building, Settings, Trash2, Edit3,
  Sparkles
} from 'lucide-react'
import clsx from 'clsx'
import { useAuthStore } from '@/stores/authStore'
import {
  analyzerApi, assetsApi, PromptTemplate, ImageAnalysis, ElementAnalysis, Asset, API_BASE
} from '@/services/api'
import {
  getEffectivePrompt,
  sanitizePrompt
} from '@/utils/prompts'
import { ElementPanel, ElementType as ElementPanelType } from '@/components/analyzer/ElementPanel'
import { IntegratedCoverGenerationModal } from '@/components/IntegratedCoverGenerationModal'
import { ImageViewerModal } from '@/components/ImageViewerModal'
import { ReanalyzeModal } from '@/components/analyzer/ReanalyzeModal'
import { AnalysisHistoryModal } from '@/components/analyzer/AnalysisHistoryModal'

// AI 분석 모델 목록 (이미지 분석용)
const AI_MODELS = [
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: '빠른 분석, 일반 용도' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: '고품질 분석, 상세한 결과' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: '균형 잡힌 속도와 품질' },
  { id: 'claude-sonnet', name: 'Claude Sonnet', description: '정교한 분석' },
]

// 이미지 생성 모델 목록 (재생성용)
const IMAGE_GENERATION_MODELS = [
  { id: 'gemini-2.5-flash-image', name: 'Gemini 2.5 Flash Image', description: '빠르고 저렴 (추천)' },
  { id: 'gemini-3-pro-image-preview', name: 'Gemini 3 Pro Image', description: '4K 지원, 최고 품질' },
  { id: 'gemini-2.0-flash-image', name: 'Gemini 2.0 Flash Image', description: '최신 빠른 모델' },
  { id: 'gemini-1.5-flash-image', name: 'Gemini 1.5 Flash Image', description: '안정적' },
  { id: 'stable-diffusion-xl', name: 'Stable Diffusion XL', description: '고품질 이미지' },
  { id: 'stable-diffusion-3', name: 'Stable Diffusion 3', description: '최신 SD 모델' },
  { id: 'dalle-3', name: 'DALL-E 3', description: 'OpenAI 이미지 생성' },
]

// Text-to-Text 모델 목록 (AI 변형용)
const TEXT_TO_TEXT_MODELS = [
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: '빠른 텍스트 변형' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: '균형잡힌 성능' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: '고품질 변형' },
  { id: 'claude-sonnet-4-20250514', name: 'Claude Sonnet 4', description: '창의적 변형' },
]

// 6가지 요소 타입
type ElementType = 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher'

const elementConfig: Record<ElementType, { icon: typeof ImageIcon; label: string; color: string; dbKey: string }> = {
  background: { icon: Layers, label: '배경', color: 'bg-blue-500', dbKey: 'background' },
  character: { icon: User, label: '인물', color: 'bg-purple-500', dbKey: 'character' },
  title: { icon: Type, label: '제목 타이포', color: 'bg-amber-500', dbKey: 'typography' },
  subtitle: { icon: Type, label: '부제목 타이포', color: 'bg-orange-500', dbKey: 'subtitle' },
  author: { icon: BookOpen, label: '저자/실명', color: 'bg-pink-500', dbKey: 'author' },
  publisher: { icon: Building, label: '출판사', color: 'bg-emerald-500', dbKey: 'publisher' },
}

const ELEMENT_TYPES: ElementType[] = ['background', 'character', 'title', 'subtitle', 'author', 'publisher']

// Variation options for select dropdown
const VARIATION_OPTIONS = [
  { value: 0, label: '0% - 원본 유지' },
  { value: 10, label: '10%' },
  { value: 20, label: '20%' },
  { value: 30, label: '30%' },
  { value: 40, label: '40%' },
  { value: 50, label: '50% - 중간' },
  { value: 60, label: '60%' },
  { value: 70, label: '70%' },
  { value: 80, label: '80%' },
  { value: 90, label: '90%' },
  { value: 100, label: '100% - 완전히 새로' },
]


export default function AnalyzerPage() {
  const navigate = useNavigate()
  const { isAuthenticated, points } = useAuthStore()
  const [searchParams] = useSearchParams()
  const assetIdParam = searchParams.get('assetId')

  // 로그인 필수 - 비로그인시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // State
  const [sourceImage, setSourceImage] = useState<string | null>(null)
  const [sourceAsset, setSourceAsset] = useState<Asset | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const [templates, setTemplates] = useState<PromptTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string>('tpl-all-default')

  const [analysis, setAnalysis] = useState<ImageAnalysis | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analyzeError, setAnalyzeError] = useState<string | null>(null)

  // Integrated cover generation modal
  const [showIntegratedCoverModal, setShowIntegratedCoverModal] = useState(false)

  // Image viewer modal
  const [showImageViewer, setShowImageViewer] = useState(false)

  // Reanalyze modal
  const [reanalyzeElement, setReanalyzeElement] = useState<ElementType | null>(null)

  // Analysis history modal
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  // Asset 등록 확인 다이얼로그
  const [registerAssetDialog, setRegisterAssetDialog] = useState<{
    element: ElementType
    imageUrl: string
    prompt: string
    metadata: {
      name: string
      description: string
      tags: string[]
    }
  } | null>(null)

  // 확장된 요소들 (기본값: 모두 확장)
  const [expandedElements, setExpandedElements] = useState<Set<ElementType>>(
    new Set(ELEMENT_TYPES)
  )
  const [globalVariation, setGlobalVariation] = useState(0)
  const [variations, setVariations] = useState<Record<ElementType, number>>({
    background: 0,
    character: 0,
    title: 0,
    subtitle: 0,
    author: 0,
    publisher: 0,
  })
  const [customPrompts, setCustomPrompts] = useState<Record<ElementType, string>>({
    background: '',
    character: '',
    title: '',
    subtitle: '',
    author: '',
    publisher: '',
  })
  const [originalPrompts, setOriginalPrompts] = useState<Record<ElementType, string>>({
    background: '',
    character: '',
    title: '',
    subtitle: '',
    author: '',
    publisher: '',
  })
  const [selectedModels, setSelectedModels] = useState<Record<ElementType, string>>({
    background: 'gemini-2.5-flash-image',
    character: 'gemini-2.5-flash-image',
    title: 'gemini-2.5-flash-image',
    subtitle: 'gemini-2.5-flash-image',
    author: 'gemini-2.5-flash-image',
    publisher: 'gemini-2.5-flash-image',
  })

  // AI Transform (Text-to-Text) states
  const [transformModels, setTransformModels] = useState<Record<ElementType, string>>({
    background: 'gemini-2.0-flash',
    character: 'gemini-2.0-flash',
    title: 'gemini-2.0-flash',
    subtitle: 'gemini-2.0-flash',
    author: 'gemini-2.0-flash',
    publisher: 'gemini-2.0-flash',
  })
  const [transformVariations, setTransformVariations] = useState<Record<ElementType, number>>({
    background: 0,
    character: 0,
    title: 0,
    subtitle: 0,
    author: 0,
    publisher: 0,
  })
  const [isTransformingPrompt, setIsTransformingPrompt] = useState<Record<ElementType, boolean>>({
    background: false,
    character: false,
    title: false,
    subtitle: false,
    author: false,
    publisher: false,
  })

  const [isRegenerating, setIsRegenerating] = useState<Record<ElementType, boolean>>({
    background: false,
    character: false,
    title: false,
    subtitle: false,
    author: false,
    publisher: false,
  })

  // Background colors for non-background elements (배경 외 요소의 배경색)
  const [elementBackgroundColors, setElementBackgroundColors] = useState<Record<ElementType, string>>({
    background: 'none', // 배경 요소는 사용 안 함
    character: 'auto',
    title: 'auto',
    subtitle: 'auto',
    author: 'auto',
    publisher: 'auto',
  })
  const [generatedImages, setGeneratedImages] = useState<Record<ElementType, string | null>>({
    background: null,
    character: null,
    title: null,
    subtitle: null,
    author: null,
    publisher: null,
  })

  // 나만의 프롬프트 저장 관련
  const [showSavePrompt, setShowSavePrompt] = useState(false)
  const [savePromptName, setSavePromptName] = useState('')

  const [showHistory, setShowHistory] = useState(false)
  const [analysisHistory, setAnalysisHistory] = useState<ImageAnalysis[]>([])
  const [historyFilterByAsset, setHistoryFilterByAsset] = useState(false) // 현재 에셋만 필터링
  const [isLoadedFromHistory, setIsLoadedFromHistory] = useState(false) // 이전 분석 결과인지 표시

  // 추출용 프롬프트 템플릿 관리
  const [showTemplateManager, setShowTemplateManager] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [newTemplateText, setNewTemplateText] = useState('')
  const [newTemplateType, setNewTemplateType] = useState<'all' | 'background' | 'character' | 'typography' | 'effect'>('all')
  const [templateSaving, setTemplateSaving] = useState(false)

  // AI 모델 선택
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash')

  // 세부요소 편집 모달
  const [showDetailEditor, setShowDetailEditor] = useState(false)
  const [detailEditorElement, setDetailEditorElement] = useState<ElementType | null>(null)
  const [detailVariations, setDetailVariations] = useState<Record<string, number>>({})
  const [detailTexts, setDetailTexts] = useState<Record<string, string>>({})
  const [isTransforming, setIsTransforming] = useState<Record<string, boolean>>({})
  const [transformAiModel, setTransformAiModel] = useState<string>('claude-sonnet-4-20250514') // AI 변형 모델 ID
  const [availableTransformModels, setAvailableTransformModels] = useState<Array<{
    id: string
    name: string
    provider: string
    description: string
  }>>([])
  const [showModelSelector, setShowModelSelector] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // 요소 확장/축소 함수
  const toggleElement = (element: ElementType) => {
    setExpandedElements(prev => {
      const next = new Set(prev)
      if (next.has(element)) {
        next.delete(element)
      } else {
        next.add(element)
      }
      return next
    })
  }

  const expandAll = () => {
    setExpandedElements(new Set(ELEMENT_TYPES))
  }

  const collapseAll = () => {
    setExpandedElements(new Set())
  }

  // Load asset if assetId is provided
  useEffect(() => {
    if (assetIdParam) {
      loadAsset(assetIdParam)
    }
  }, [assetIdParam])

  // Load templates
  useEffect(() => {
    loadTemplates()
  }, [])

  // Load available transform models
  useEffect(() => {
    const loadTransformModels = async () => {
      const result = await analyzerApi.getTransformModels()
      if (result.success && result.data) {
        setAvailableTransformModels(result.data)
      }
    }
    loadTransformModels()
  }, [])

  // 최근 분석 세션 자동 복원 (assetId 없이 진입한 경우)
  useEffect(() => {
    if (!assetIdParam && isAuthenticated && !analysis && !sourceImage) {
      loadLastSession()
    }
  }, [assetIdParam, isAuthenticated])

  // 배경색 변경 시 프롬프트 자동 업데이트
  useEffect(() => {
    ELEMENT_TYPES.forEach((element) => {
      if (element === 'background') return // 배경 요소는 제외

      const currentPrompt = customPrompts[element]
      if (!currentPrompt) return

      const bgColor = elementBackgroundColors[element]

      // 기존 배경 관련 지시문 제거
      let updatedPrompt = currentPrompt.replace(/\n*중요:\s*배경[^]*?(?=\n\n|$)/g, '').trim()

      // 새 배경 지시문 생성
      let bgInstruction = ''
      switch (bgColor) {
        case 'auto':
          bgInstruction = '\n\n중요: 배경은 단색으로 피사체와 분리되어 제거하기 용이하게 할것'
          break
        case 'white':
          bgInstruction = '\n\n중요: 배경은 순백색(#FFFFFF)으로 피사체와 명확히 구분되게 할것'
          break
        case 'black':
          bgInstruction = '\n\n중요: 배경은 순검정색(#000000)으로 피사체와 명확히 구분되게 할것'
          break
        case 'gray':
          bgInstruction = '\n\n중요: 배경은 중간 회색(#808080)으로 피사체와 명확히 구분되게 할것'
          break
        case 'green':
          bgInstruction = '\n\n중요: 배경은 크로마키 그린(#00FF00)으로 피사체와 명확히 구분되게 할것'
          break
      }

      const finalPrompt = updatedPrompt + bgInstruction

      // 프롬프트가 실제로 변경된 경우에만 업데이트
      if (finalPrompt !== currentPrompt) {
        console.log(`[배경색 변경] ${elementConfig[element].label} - ${bgColor}`)
        console.log('업데이트된 프롬프트:', finalPrompt)
        setCustomPrompts(prev => ({ ...prev, [element]: finalPrompt }))
      }
    })
  }, [elementBackgroundColors])

  // 마지막 분석 세션 복원
  const loadLastSession = async () => {
    try {
      const result = await analyzerApi.listAnalyses({ limit: 1 })
      if (result.success && result.data && result.data.length > 0) {
        const lastAnalysis = result.data[0]

        // 분석 정보 설정
        setAnalysis(lastAnalysis)
        setIsLoadedFromHistory(true)

        // Asset 정보 로드
        if (lastAnalysis.asset_id) {
          const assetResult = await assetsApi.get(lastAnalysis.asset_id)
          if (assetResult.success && assetResult.data) {
            setSourceAsset(assetResult.data)
            setSourceImage(`${API_BASE}/assets/${lastAnalysis.asset_id}/image`)
          }
        } else if (lastAnalysis.source_image_url) {
          // asset이 없으면 source_image_url 사용
          setSourceImage(lastAnalysis.source_image_url)
        }

        // 프롬프트 복원
        const prompts = {
          background: getEffectivePrompt('background', {
            final: lastAnalysis.final_background_prompt,
            generated: lastAnalysis.background_prompt,
            analysis: lastAnalysis.background_analysis
          }),
          character: getEffectivePrompt('character', {
            final: lastAnalysis.final_character_prompt,
            generated: lastAnalysis.character_prompt,
            analysis: lastAnalysis.character_analysis
          }),
          title: getEffectivePrompt('title', {
            final: lastAnalysis.final_typography_prompt,
            generated: lastAnalysis.typography_prompt,
            analysis: lastAnalysis.typography_analysis
          }),
          subtitle: getEffectivePrompt('subtitle', {
            final: lastAnalysis.final_subtitle_prompt,
            generated: lastAnalysis.subtitle_prompt,
            analysis: lastAnalysis.subtitle_analysis
          }),
          author: getEffectivePrompt('author', {
            final: lastAnalysis.final_author_prompt,
            generated: lastAnalysis.author_prompt,
            analysis: lastAnalysis.author_analysis
          }),
          publisher: getEffectivePrompt('publisher', {
            final: lastAnalysis.final_publisher_prompt,
            generated: lastAnalysis.publisher_prompt,
            analysis: lastAnalysis.publisher_analysis
          }),
        }
        setCustomPrompts(prompts)
        // For history, use generated prompts as original (since that's what analysis produced)
        setOriginalPrompts({
          background: getEffectivePrompt('background', {
            generated: lastAnalysis.background_prompt,
            analysis: lastAnalysis.background_analysis
          }),
          character: getEffectivePrompt('character', {
            generated: lastAnalysis.character_prompt,
            analysis: lastAnalysis.character_analysis
          }),
          title: getEffectivePrompt('title', {
            generated: lastAnalysis.typography_prompt,
            analysis: lastAnalysis.typography_analysis
          }),
          subtitle: getEffectivePrompt('subtitle', {
            generated: lastAnalysis.subtitle_prompt,
            analysis: lastAnalysis.subtitle_analysis
          }),
          author: getEffectivePrompt('author', {
            generated: lastAnalysis.author_prompt,
            analysis: lastAnalysis.author_analysis
          }),
          publisher: getEffectivePrompt('publisher', {
            generated: lastAnalysis.publisher_prompt,
            analysis: lastAnalysis.publisher_analysis
          }),
        })

        // 생성된 이미지 로드 (generated_*_id가 있으면)
        const generatedImageUrls: Record<ElementType, string | null> = {
          background: lastAnalysis.generated_background_id ? `${API_BASE}/assets/${lastAnalysis.generated_background_id}/image` : null,
          character: lastAnalysis.generated_character_id ? `${API_BASE}/assets/${lastAnalysis.generated_character_id}/image` : null,
          title: lastAnalysis.generated_typography_id ? `${API_BASE}/assets/${lastAnalysis.generated_typography_id}/image` : null,
          subtitle: lastAnalysis.generated_subtitle_id ? `${API_BASE}/assets/${lastAnalysis.generated_subtitle_id}/image` : null,
          author: lastAnalysis.generated_author_id ? `${API_BASE}/assets/${lastAnalysis.generated_author_id}/image` : null,
          publisher: lastAnalysis.generated_publisher_id ? `${API_BASE}/assets/${lastAnalysis.generated_publisher_id}/image` : null,
        }
        console.log('[AnalyzerPage] Generated IDs from API:', {
          background: lastAnalysis.generated_background_id,
          character: lastAnalysis.generated_character_id,
          title: lastAnalysis.generated_typography_id,
          subtitle: lastAnalysis.generated_subtitle_id,
          author: lastAnalysis.generated_author_id,
          publisher: lastAnalysis.generated_publisher_id,
        })
        console.log('[AnalyzerPage] Generated image URLs:', generatedImageUrls)
        setGeneratedImages(generatedImageUrls)

        // 저장된 모델 로드
        const savedModels: Record<ElementType, string> = {
          background: lastAnalysis.generated_background_model || 'gemini-2.5-flash-image',
          character: lastAnalysis.generated_character_model || 'gemini-2.5-flash-image',
          title: lastAnalysis.generated_typography_model || 'gemini-2.5-flash-image',
          subtitle: lastAnalysis.generated_subtitle_model || 'gemini-2.5-flash-image',
          author: lastAnalysis.generated_author_model || 'gemini-2.5-flash-image',
          publisher: lastAnalysis.generated_publisher_model || 'gemini-2.5-flash-image',
        }
        setSelectedModels(savedModels)
        console.log('[AnalyzerPage] Loaded saved models:', savedModels)

        console.log('[AnalyzerPage] Last session restored:', lastAnalysis.id)
      }
    } catch (err) {
      console.error('[AnalyzerPage] Failed to load last session:', err)
    }
  }

  const loadAsset = async (id: string) => {
    const result = await assetsApi.get(id)
    if (result.success && result.data) {
      setSourceAsset(result.data)
      setSourceImage(`${API_BASE}/assets/${id}/image`)

      // 해당 에셋의 최근 분석 이력 자동 로드
      loadLatestAnalysisForAsset(id)
    }
  }

  // 에셋의 최근 분석 이력 자동 로드
  const loadLatestAnalysisForAsset = async (assetId: string) => {
    try {
      const result = await analyzerApi.listAnalyses({ limit: 1, assetId })
      if (result.success && result.data && result.data.length > 0) {
        const latestAnalysis = result.data[0]
        setAnalysis(latestAnalysis)
        setIsLoadedFromHistory(true)

        // 프롬프트 설정 (우선순위: final > generated > template)
        const prompts = {
          background: getEffectivePrompt('background', {
            final: latestAnalysis.final_background_prompt,
            generated: latestAnalysis.background_prompt,
            analysis: latestAnalysis.background_analysis
          }),
          character: getEffectivePrompt('character', {
            final: latestAnalysis.final_character_prompt,
            generated: latestAnalysis.character_prompt,
            analysis: latestAnalysis.character_analysis
          }),
          title: getEffectivePrompt('title', {
            final: latestAnalysis.final_typography_prompt,
            generated: latestAnalysis.typography_prompt,
            analysis: latestAnalysis.typography_analysis
          }),
          subtitle: getEffectivePrompt('subtitle', {
            final: latestAnalysis.final_subtitle_prompt,
            generated: latestAnalysis.subtitle_prompt,
            analysis: latestAnalysis.subtitle_analysis
          }),
          author: getEffectivePrompt('author', {
            final: latestAnalysis.final_author_prompt,
            generated: latestAnalysis.author_prompt,
            analysis: latestAnalysis.author_analysis
          }),
          publisher: getEffectivePrompt('publisher', {
            final: latestAnalysis.final_publisher_prompt,
            generated: latestAnalysis.publisher_prompt,
            analysis: latestAnalysis.publisher_analysis
          }),
        }
        setCustomPrompts(prompts)

        // 생성된 이미지 로드 (generated_*_id가 있으면)
        const generatedImageUrls: Record<ElementType, string | null> = {
          background: latestAnalysis.generated_background_id ? `${API_BASE}/assets/${latestAnalysis.generated_background_id}/image` : null,
          character: latestAnalysis.generated_character_id ? `${API_BASE}/assets/${latestAnalysis.generated_character_id}/image` : null,
          title: latestAnalysis.generated_typography_id ? `${API_BASE}/assets/${latestAnalysis.generated_typography_id}/image` : null,
          subtitle: latestAnalysis.generated_subtitle_id ? `${API_BASE}/assets/${latestAnalysis.generated_subtitle_id}/image` : null,
          author: latestAnalysis.generated_author_id ? `${API_BASE}/assets/${latestAnalysis.generated_author_id}/image` : null,
          publisher: latestAnalysis.generated_publisher_id ? `${API_BASE}/assets/${latestAnalysis.generated_publisher_id}/image` : null,
        }
        console.log('[loadLatestAnalysisForAsset] Generated IDs:', {
          background: latestAnalysis.generated_background_id,
          character: latestAnalysis.generated_character_id,
          title: latestAnalysis.generated_typography_id,
        })
        console.log('[loadLatestAnalysisForAsset] Generated image URLs:', generatedImageUrls)
        setGeneratedImages(generatedImageUrls)

        // 저장된 모델 로드
        const savedModels: Record<ElementType, string> = {
          background: latestAnalysis.generated_background_model || 'gemini-2.5-flash-image',
          character: latestAnalysis.generated_character_model || 'gemini-2.5-flash-image',
          title: latestAnalysis.generated_typography_model || 'gemini-2.5-flash-image',
          subtitle: latestAnalysis.generated_subtitle_model || 'gemini-2.5-flash-image',
          author: latestAnalysis.generated_author_model || 'gemini-2.5-flash-image',
          publisher: latestAnalysis.generated_publisher_model || 'gemini-2.5-flash-image',
        }
        setSelectedModels(savedModels)

        // For history, use generated prompts as original
        setOriginalPrompts({
          background: getEffectivePrompt('background', {
            generated: latestAnalysis.background_prompt,
            analysis: latestAnalysis.background_analysis
          }),
          character: getEffectivePrompt('character', {
            generated: latestAnalysis.character_prompt,
            analysis: latestAnalysis.character_analysis
          }),
          title: getEffectivePrompt('title', {
            generated: latestAnalysis.typography_prompt,
            analysis: latestAnalysis.typography_analysis
          }),
          subtitle: getEffectivePrompt('subtitle', {
            generated: latestAnalysis.subtitle_prompt,
            analysis: latestAnalysis.subtitle_analysis
          }),
          author: getEffectivePrompt('author', {
            generated: latestAnalysis.author_prompt,
            analysis: latestAnalysis.author_analysis
          }),
          publisher: getEffectivePrompt('publisher', {
            generated: latestAnalysis.publisher_prompt,
            analysis: latestAnalysis.publisher_analysis
          }),
        })
      }
    } catch (err) {
      console.error('Failed to load latest analysis:', err)
    }
  }

  const loadTemplates = async () => {
    const result = await analyzerApi.listTemplates()
    if (result.success && result.data) {
      setTemplates(result.data)
    }
  }

  const loadHistory = async (filterByAsset = historyFilterByAsset) => {
    const options: { limit: number; assetId?: string } = { limit: 20 }
    if (filterByAsset && sourceAsset?.id) {
      options.assetId = sourceAsset.id
    }
    const result = await analyzerApi.listAnalyses(options)
    if (result.success && result.data) {
      setAnalysisHistory(result.data)
    }
  }

  // 템플릿 저장
  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateText.trim()) return
    setTemplateSaving(true)

    try {
      if (editingTemplate) {
        // 수정
        const result = await analyzerApi.updateTemplate(editingTemplate.id, {
          name: newTemplateName,
          templateText: newTemplateText,
        })
        if (result.success) {
          await loadTemplates()
          setEditingTemplate(null)
          setNewTemplateName('')
          setNewTemplateText('')
        }
      } else {
        // 새로 생성
        const result = await analyzerApi.createTemplate({
          name: newTemplateName,
          elementType: newTemplateType,
          templateText: newTemplateText,
          isPublic: false,
        })
        if (result.success) {
          await loadTemplates()
          setNewTemplateName('')
          setNewTemplateText('')
        }
      }
    } catch (err) {
      console.error('Failed to save template:', err)
    } finally {
      setTemplateSaving(false)
    }
  }

  // 템플릿 삭제
  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('이 템플릿을 삭제하시겠습니까?')) return

    try {
      const result = await analyzerApi.deleteTemplate(id)
      if (result.success) {
        await loadTemplates()
        if (selectedTemplate === id) {
          setSelectedTemplate('tpl-all-default')
        }
      }
    } catch (err) {
      console.error('Failed to delete template:', err)
    }
  }

  // 템플릿 편집 시작
  const startEditTemplate = (template: PromptTemplate) => {
    setEditingTemplate(template)
    setNewTemplateName(template.name)
    setNewTemplateText(template.template_text)
    setNewTemplateType(template.element_type)
  }

  // 템플릿 편집 취소
  const cancelEditTemplate = () => {
    setEditingTemplate(null)
    setNewTemplateName('')
    setNewTemplateText('')
    setNewTemplateType('all')
  }

  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadedFile(file)
      setSourceAsset(null)
      const reader = new FileReader()
      reader.onload = () => setSourceImage(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  // Analyze image
  const handleAnalyze = async () => {
    setIsAnalyzing(true)
    setAnalyzeError(null)

    try {
      let result
      if (sourceAsset) {
        result = await analyzerApi.analyzeAsset(sourceAsset.id, {
          templateId: selectedTemplate,
          model: selectedModel,
        })
      } else if (uploadedFile) {
        result = await analyzerApi.analyzeImage(uploadedFile, {
          templateId: selectedTemplate,
          model: selectedModel,
        })
      } else {
        setAnalyzeError('분석할 이미지를 선택해주세요.')
        setIsAnalyzing(false)
        return
      }

      if (result.success && result.data) {
        setAnalysis(result.data)
        setIsLoadedFromHistory(false) // 새로운 분석

        // Set initial prompts from analysis (6개 요소)
        const prompts = {
          background: getEffectivePrompt('background', {
            generated: result.data.background_prompt,
            analysis: result.data.background_analysis
          }),
          character: getEffectivePrompt('character', {
            generated: result.data.character_prompt,
            analysis: result.data.character_analysis
          }),
          title: getEffectivePrompt('title', {
            generated: result.data.typography_prompt,
            analysis: result.data.typography_analysis
          }),
          subtitle: getEffectivePrompt('subtitle', {
            generated: result.data.subtitle_prompt,
            analysis: result.data.subtitle_analysis
          }),
          author: getEffectivePrompt('author', {
            generated: result.data.author_prompt,
            analysis: result.data.author_analysis
          }),
          publisher: getEffectivePrompt('publisher', {
            generated: result.data.publisher_prompt,
            analysis: result.data.publisher_analysis
          }),
        }
        setCustomPrompts(prompts)
        setOriginalPrompts(prompts) // Save as original for diff comparison
      } else {
        setAnalyzeError(result.error?.message || '분석에 실패했습니다.')
      }
    } catch (err) {
      setAnalyzeError('분석 중 오류가 발생했습니다.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Apply variation to prompt
  const applyVariation = (_element: ElementType, basePrompt: string, variation: number): string => {
    if (variation === 0) return basePrompt

    const modifiers = [
      'slightly different style',
      'with some variations',
      'reimagined with creative changes',
      'dramatically transformed',
      'completely reinterpreted'
    ]

    const modifierIndex = Math.min(Math.floor(variation / 25), 4)
    return `${basePrompt}, ${modifiers[modifierIndex]} (variation: ${variation}%)`
  }

  // Transform prompt using AI (Text-to-Text)
  const handleTransformPrompt = async (element: ElementType) => {
    if (!analysis) return

    const variation = transformVariations[element]
    if (variation === 0) {
      setAnalyzeError('변형 강도를 선택해주세요.')
      return
    }

    setIsTransformingPrompt(prev => ({ ...prev, [element]: true }))

    try {
      const currentText = customPrompts[element] || originalPrompts[element] || ''

      if (!currentText.trim()) {
        setAnalyzeError('변형할 프롬프트가 없습니다.')
        setIsTransformingPrompt(prev => ({ ...prev, [element]: false }))
        return
      }

      // Call transform API
      const result = await analyzerApi.transformText({
        text: currentText,
        variation,
        field: 'description',
        elementType: element,
        model: transformModels[element],
      })

      if (result.success && result.data?.transformedText) {
        // Update prompt with transformed text
        setCustomPrompts(prev => ({
          ...prev,
          [element]: result.data!.transformedText
        }))
      } else {
        setAnalyzeError(result.error?.message || 'AI 변형에 실패했습니다.')
      }
    } catch (err) {
      console.error('Transform error:', err)
      setAnalyzeError('AI 변형 중 오류가 발생했습니다.')
    } finally {
      setIsTransformingPrompt(prev => ({ ...prev, [element]: false }))
    }
  }

  // Regenerate element
  const handleRegenerate = async (element: ElementType) => {
    if (!analysis) return

    setIsRegenerating(prev => ({ ...prev, [element]: true }))

    try {
      const basePrompt = customPrompts[element] || (analysis as any)[`${element}_prompt`] || ''

      // 프롬프트 검증
      if (!basePrompt.trim()) {
        setAnalyzeError('프롬프트를 입력해주세요.')
        setIsRegenerating(prev => ({ ...prev, [element]: false }))
        return
      }

      // 변형 적용
      let finalPrompt = applyVariation(element, basePrompt, variations[element])

      // 배경색 지시 추가 (배경 제외)
      if (element !== 'background') {
        // 기존 배경 관련 지시문 제거
        finalPrompt = finalPrompt.replace(/\n*중요:\s*배경[^]*?(?=\n\n|$)/g, '').trim()

        const bgColor = elementBackgroundColors[element]
        let bgInstruction = ''

        switch (bgColor) {
          case 'auto':
            bgInstruction = '\n\n중요: 배경은 단색으로 피사체와 분리되어 제거하기 용이하게 할것'
            break
          case 'white':
            bgInstruction = '\n\n중요: 배경은 순백색(#FFFFFF)으로 피사체와 명확히 구분되게 할것'
            break
          case 'black':
            bgInstruction = '\n\n중요: 배경은 순검정색(#000000)으로 피사체와 명확히 구분되게 할것'
            break
          case 'gray':
            bgInstruction = '\n\n중요: 배경은 중간 회색(#808080)으로 피사체와 명확히 구분되게 할것'
            break
          case 'green':
            bgInstruction = '\n\n중요: 배경은 크로마키 그린(#00FF00)으로 피사체와 명확히 구분되게 할것'
            break
        }

        finalPrompt = finalPrompt + bgInstruction
      }

      // 프롬프트 위생 검사 (중복 제거)
      finalPrompt = sanitizePrompt(finalPrompt)

      console.log(`[재생성] ${elementConfig[element].label} - 전송될 최종 프롬프트:`)
      console.log(finalPrompt)

      const result = await analyzerApi.regenerate(analysis.id, {
        elementType: element,
        prompt: finalPrompt,
        model: selectedModels[element],
        aspectRatio: '3:4',
        backgroundColor: element !== 'background' ? elementBackgroundColors[element] : undefined,
      })

      if (result.success && result.data) {
        // 캐시 무효화를 위해 timestamp 추가
        const imageUrl = `${API_BASE}${result.data!.imageUrl}?t=${Date.now()}`
        setGeneratedImages(prev => ({
          ...prev,
          [element]: imageUrl
        }))

        // Asset은 이미 백엔드에서 자동 저장됨
        alert(`${elementConfig[element].label} 이미지가 생성되어 Asset에 자동 저장되었습니다!`)
      } else {
        setAnalyzeError(result.error?.message || '재생성에 실패했습니다.')
      }
    } catch (err) {
      setAnalyzeError('재생성 중 오류가 발생했습니다.')
    } finally {
      setIsRegenerating(prev => ({ ...prev, [element]: false }))
    }
  }

  // Download generated image
  const handleDownloadImage = async (element: ElementType) => {
    const imageUrl = generatedImages[element]
    if (!imageUrl) return

    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${element}-generated-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
      setAnalyzeError('이미지 다운로드에 실패했습니다.')
    }
  }

  // Prepare asset registration (show dialog)
  const handleRegisterAsset = async (element: ElementType) => {
    const imageUrl = generatedImages[element]
    const promptKey = getPromptKey(element)
    const prompt = customPrompts[element] || (analysis as any)?.[promptKey] || ''

    if (!imageUrl || !prompt || !analysis?.id) {
      setAnalyzeError('이미지 또는 프롬프트가 없습니다.')
      return
    }

    try {
      setIsAnalyzing(true)
      setAnalyzeError(null)

      // 원본 asset 정보 가져오기
      let sourceAsset = null
      if (analysis.asset_id) {
        try {
          const assetResponse = await assetsApi.get(analysis.asset_id)
          if (assetResponse.success && assetResponse.data) {
            sourceAsset = assetResponse.data
          }
        } catch (err) {
          console.log('Failed to fetch source asset:', err)
        }
      }

      // 프롬프트에서 키워드 추출 (간단한 파싱)
      const extractKeywords = (text: string): string[] => {
        // 쉼표, 세미콜론, 'and', 'with' 등으로 구분
        const keywords = text
          .toLowerCase()
          .split(/[,;]|\band\b|\bwith\b|\bin\b/)
          .map(k => k.trim())
          .filter(k => k.length > 2 && k.length < 30) // 너무 짧거나 긴 키워드 제외
          .slice(0, 10) // 최대 10개
        return keywords
      }

      // Asset 메타데이터 생성
      let assetName = `${elementConfig[element].label} - ${new Date().toLocaleDateString()}`
      let assetDescription = `AI 분석기에서 생성된 ${elementConfig[element].label}`
      let assetTags = [element, 'ai-generated']

      // 원본 asset이 있으면 정보 활용
      if (sourceAsset) {
        // 원본 제목 활용
        if (sourceAsset.name) {
          assetName = `${sourceAsset.name} - ${elementConfig[element].label}`
        }
        // 원본 설명에 추가 정보 포함
        if (sourceAsset.description) {
          assetDescription = `${sourceAsset.description}에서 추출된 ${elementConfig[element].label}`
        }
      }

      // 프롬프트에서 키워드 추출하여 태그에 추가
      const promptKeywords = extractKeywords(prompt)
      assetTags = [...assetTags, ...promptKeywords]

      // 중복 제거
      assetTags = Array.from(new Set(assetTags))

      // 다이얼로그 표시 (사용자가 확인/수정할 수 있도록)
      setRegisterAssetDialog({
        element,
        imageUrl,
        prompt,
        metadata: {
          name: assetName,
          description: assetDescription,
          tags: assetTags
        }
      })
    } catch (err) {
      console.error('Prepare asset registration failed:', err)
      setAnalyzeError(`메타데이터 생성 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Confirm and register asset (after user confirmation)
  const handleConfirmRegisterAsset = async () => {
    if (!registerAssetDialog || !analysis?.id) return

    try {
      setIsAnalyzing(true)
      setAnalyzeError(null)

      const { element, imageUrl, prompt, metadata } = registerAssetDialog

      const response = await analyzerApi.registerElementAsAsset(analysis.id, {
        element,
        imageUrl,
        prompt,
        assetMetadata: {
          name: metadata.name,
          description: metadata.description,
          tags: metadata.tags,
          isPublic: false
        }
      })

      if (response.success && response.data) {
        const status = response.data.vectorizationStatus
        const message = status === 'success'
          ? `Asset으로 등록되었습니다! (벡터화 완료)\n\n등록된 키워드: ${metadata.tags.join(', ')}`
          : status === 'failed'
          ? `Asset으로 등록되었습니다. (벡터화 실패: ${response.data.vectorError})`
          : `Asset으로 등록되었습니다. (벡터화 건너뜀)`

        alert(message)

        // 다이얼로그 닫기
        setRegisterAssetDialog(null)

        // Asset 페이지로 이동할지 물어보기
        const goToAssets = window.confirm('등록된 Asset을 보러 가시겠습니까?')
        if (goToAssets) {
          navigate('/assets')
        }
      } else {
        throw new Error(response.error?.message || '등록 실패')
      }
    } catch (err) {
      console.error('Register asset failed:', err)
      setAnalyzeError(`Asset 등록 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Get analysis result for element
  const getElementAnalysis = (element: ElementType): ElementAnalysis | null => {
    if (!analysis) return null
    // 요소별 DB 컬럼 매핑 사용
    const config = elementConfig[element]
    const analysisKey = `${config.dbKey}Analysis`
    return (analysis as any)[analysisKey] || null
  }

  // Get prompt key for element
  const getPromptKey = (element: ElementType): string => {
    const config = elementConfig[element]
    return `${config.dbKey}_prompt`
  }

  // 세부요소 편집 모달 열기 (currently unused, kept for future feature)
  // @ts-ignore - Unused but kept for future feature
  const openDetailEditor = (element: ElementType) => {
    const elementAnalysis = getElementAnalysis(element)
    if (!elementAnalysis) return

    // 초기값 설정
    const initialTexts: Record<string, string> = {}
    const initialVariations: Record<string, number> = {}

    if (elementAnalysis.description) {
      initialTexts.description = elementAnalysis.description
      initialVariations.description = 0
    }
    if (elementAnalysis.style) {
      initialTexts.style = elementAnalysis.style
      initialVariations.style = 0
    }
    if (elementAnalysis.mood) {
      initialTexts.mood = elementAnalysis.mood
      initialVariations.mood = 0
    }
    if (elementAnalysis.colors) {
      initialTexts.colors = Array.isArray(elementAnalysis.colors)
        ? elementAnalysis.colors.join(', ')
        : elementAnalysis.colors
      initialVariations.colors = 0
    }
    if (elementAnalysis.composition) {
      initialTexts.composition = elementAnalysis.composition
      initialVariations.composition = 0
    }
    if (elementAnalysis.lighting) {
      initialTexts.lighting = elementAnalysis.lighting
      initialVariations.lighting = 0
    }

    setDetailTexts(initialTexts)
    setDetailVariations(initialVariations)
    setDetailEditorElement(element)
    setShowDetailEditor(true)
  }

  // AI로 세부요소 텍스트 변형
  const transformDetailText = async (field: string) => {
    if (!detailTexts[field] || !detailEditorElement) return

    const variation = detailVariations[field] || 0
    if (variation === 0) return

    setIsTransforming(prev => ({ ...prev, [field]: true }))

    try {
      const result = await analyzerApi.transformText({
        text: detailTexts[field],
        variation,
        field,
        elementType: detailEditorElement,
        model: transformAiModel, // Claude 또는 Gemini 선택
      })

      if (result.success && result.data?.transformedText) {
        setDetailTexts(prev => ({ ...prev, [field]: result.data!.transformedText }))
      }
    } catch (err) {
      console.error('Failed to transform text:', err)
    } finally {
      setIsTransforming(prev => ({ ...prev, [field]: false }))
    }
  }

  // 세부요소 편집 적용
  const applyDetailEdits = () => {
    if (!detailEditorElement) return

    // 세부요소들을 합쳐서 프롬프트 생성
    const parts: string[] = []

    if (detailTexts.description) parts.push(detailTexts.description)
    if (detailTexts.style) parts.push(`스타일: ${detailTexts.style}`)
    if (detailTexts.mood) parts.push(`분위기: ${detailTexts.mood}`)
    if (detailTexts.colors) parts.push(`색상: ${detailTexts.colors}`)
    if (detailTexts.composition) parts.push(`구도: ${detailTexts.composition}`)
    if (detailTexts.lighting) parts.push(`조명: ${detailTexts.lighting}`)

    const newPrompt = parts.join('. ')
    setCustomPrompts(prev => ({ ...prev, [detailEditorElement]: newPrompt }))
    setShowDetailEditor(false)
  }

  // 세부요소 필드 정보
  const detailFieldConfig: Record<string, { label: string; placeholder: string }> = {
    description: { label: '설명', placeholder: '요소에 대한 설명...' },
    style: { label: '스타일', placeholder: '아트 스타일, 기법...' },
    mood: { label: '분위기', placeholder: '감정, 톤, 무드...' },
    colors: { label: '색상', placeholder: '주요 색상들...' },
    composition: { label: '구도', placeholder: '배치, 레이아웃...' },
    lighting: { label: '조명', placeholder: '조명 스타일, 방향...' },
  }

  // Render element panel using ElementPanel component
  const renderElementPanel = (element: ElementType) => {
    return (
      <ElementPanel
        key={element}
        element={element as ElementPanelType}
        config={elementConfig[element]}
        isExpanded={expandedElements.has(element)}
        onToggle={() => toggleElement(element)}
        elementAnalysis={getElementAnalysis(element)}
        originalPrompt={originalPrompts[element]}
        currentPrompt={customPrompts[element]}
        onPromptChange={(value) => setCustomPrompts(prev => ({ ...prev, [element]: value }))}
        selectedModel={selectedModels[element]}
        onModelChange={(value) => setSelectedModels(prev => ({ ...prev, [element]: value }))}
        variation={variations[element]}
        onVariationChange={(value) => setVariations(prev => ({ ...prev, [element]: value }))}
        globalVariation={globalVariation}
        generatedImage={generatedImages[element]}
        isGenerating={isRegenerating[element]}
        onRegenerate={() => handleRegenerate(element)}
        onDownload={() => handleDownloadImage(element)}
        onSaveAsset={() => handleRegisterAsset(element)}
        onRemoveImage={() => setGeneratedImages(prev => ({ ...prev, [element]: null }))}
        transformModel={transformModels[element]}
        onTransformModelChange={(value) => setTransformModels(prev => ({ ...prev, [element]: value }))}
        onTransform={() => handleTransformPrompt(element)}
        isTransforming={isTransformingPrompt[element]}
        transformVariation={transformVariations[element]}
        onTransformVariationChange={(value) => setTransformVariations(prev => ({ ...prev, [element]: value }))}
        onReanalyze={analysis ? () => setReanalyzeElement(element) : undefined}
        backgroundColor={element !== 'background' ? elementBackgroundColors[element] : undefined}
        onBackgroundColorChange={element !== 'background' ? (value) => setElementBackgroundColors(prev => ({ ...prev, [element]: value })) : undefined}
        models={IMAGE_GENERATION_MODELS}
        transformModels={TEXT_TO_TEXT_MODELS}
        variationOptions={VARIATION_OPTIONS}
      />
    )
  }


  return (
    <div className="min-h-[calc(100vh-56px)] flex">
      {/* Left Panel - Source Image */}
      <div className="w-80 border-r border-border-subtle p-4 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">원본 이미지</h2>
          <button
            onClick={() => setShowHistoryModal(true)}
            className="btn btn-secondary btn-sm flex items-center gap-1.5"
            title="분석 이력 보기"
          >
            <History className="w-4 h-4" />
            이력
          </button>
        </div>

        {/* Image Upload Area */}
        <div
          onClick={() => !sourceImage && fileInputRef.current?.click()}
          className={clsx(
            'aspect-[3/4] rounded-lg border-2 border-dashed transition-colors',
            'flex items-center justify-center overflow-hidden',
            sourceImage
              ? 'border-transparent'
              : 'border-border-default hover:border-accent-gold cursor-pointer'
          )}
        >
          {sourceImage ? (
            <div className="relative w-full h-full group">
              <img
                src={sourceImage}
                alt="Source"
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setShowImageViewer(true)}
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <ImageIcon className="w-8 h-8 mx-auto text-white mb-2" />
                  <p className="text-sm text-white font-medium">클릭하여 확대</p>
                  <p className="text-xs text-neutral-300 mt-1">휠로 줌, 드래그로 이동</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center p-4">
              <Upload className="w-10 h-10 mx-auto text-text-tertiary mb-2" />
              <p className="text-sm text-text-secondary">클릭하여 이미지 업로드</p>
              <p className="text-xs text-text-tertiary mt-1">또는 Asset에서 선택</p>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Source Info */}
        {sourceAsset && (
          <div className="mt-3 p-3 bg-bg-elevated rounded-lg text-sm">
            <p className="font-medium truncate">{sourceAsset.name}</p>
            <p className="text-text-tertiary capitalize">{sourceAsset.type}</p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 space-y-2">
          {sourceImage && (
            <button
              onClick={() => {
                setSourceImage(null)
                setSourceAsset(null)
                setUploadedFile(null)
                setAnalysis(null)
                setIsLoadedFromHistory(false)
              }}
              className="btn btn-secondary w-full text-sm"
            >
              <X className="w-4 h-4 mr-2" />
              이미지 제거
            </button>
          )}

          <Link to="/assets" className="btn btn-secondary w-full text-sm">
            <ImagePlus className="w-4 h-4 mr-2" />
            Asset에서 선택
          </Link>
        </div>

        {/* Template Selection */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">추출용 프롬프트</label>
            <button
              onClick={() => setShowTemplateManager(true)}
              className="p-1 hover:bg-bg-elevated rounded transition-colors"
              title="템플릿 관리"
            >
              <Settings className="w-4 h-4 text-text-tertiary" />
            </button>
          </div>
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="input w-full text-sm"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} {t.is_system ? '(시스템)' : '(나의 템플릿)'}
              </option>
            ))}
          </select>
        </div>

        {/* AI Model Selection */}
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2">AI 분석 모델</label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="input w-full text-sm"
          >
            {AI_MODELS.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-text-tertiary mt-1">
            {AI_MODELS.find(m => m.id === selectedModel)?.description}
          </p>
        </div>

        {/* Analyze Button */}
        <button
          onClick={handleAnalyze}
          disabled={!sourceImage || isAnalyzing}
          className="btn btn-primary w-full mt-4"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              분석 중...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              이미지 분석
            </>
          )}
        </button>

        {/* Error Message */}
        {analyzeError && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
            {analyzeError}
          </div>
        )}

        {/* Points Info */}
        <div className="mt-auto pt-4 border-t border-border-subtle">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-tertiary">보유 포인트</span>
            <span className="font-bold text-accent-gold">{points.toLocaleString()} P</span>
          </div>
        </div>
      </div>

      {/* Main Content - Element Panels */}
      <div className="flex-1 p-6 overflow-auto">
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-2xl font-bold">이미지 분석기</h1>
              <div className="flex items-center gap-3 mt-1">
                <p className="text-text-secondary text-sm">
                  이미지를 분석하고 요소별로 새로운 이미지를 생성하세요
                </p>
                {analysis && isLoadedFromHistory && (
                  <span className="text-xs text-amber-400 flex items-center gap-1">
                    <History className="w-3 h-3" />
                    {new Date(analysis.created_at).toLocaleDateString('ko-KR')} 분석결과
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Global Variation Select (moved here) */}
              {analysis && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-text-tertiary whitespace-nowrap">글로벌 변경율:</label>
                  <select
                    value={globalVariation}
                    onChange={(e) => setGlobalVariation(parseInt(e.target.value))}
                    className="input text-sm py-1 px-2 w-32"
                  >
                    {VARIATION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Integrated Cover Generation Button */}
              {analysis && (
                <button
                  onClick={() => setShowIntegratedCoverModal(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  통합이미지 생성
                </button>
              )}

              <button
                onClick={() => {
                  setShowHistory(true)
                  loadHistory()
                }}
                className="btn btn-secondary"
              >
                <History className="w-4 h-4 mr-2" />
                분석 이력
              </button>
            </div>
          </div>

          {/* Element Panels */}
          {!analysis ? (
            <div className="text-center py-20">
              <ImageIcon className="w-16 h-16 mx-auto text-text-tertiary mb-4" />
              <h2 className="text-xl font-semibold mb-2">이미지를 분석해주세요</h2>
              <p className="text-text-secondary">
                왼쪽 패널에서 이미지를 업로드하고 "이미지 분석" 버튼을 클릭하세요
              </p>
            </div>
          ) : (
            <div>
              {/* 확장/축소 제어 패널 */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-border-subtle">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Layers className="w-5 h-5 text-accent-gold" />
                  요소별 편집
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={expandAll}
                    className="btn btn-secondary text-sm px-3 py-1.5"
                  >
                    전체 펼치기
                  </button>
                  <button
                    onClick={collapseAll}
                    className="btn btn-secondary text-sm px-3 py-1.5"
                  >
                    전체 접기
                  </button>
                </div>
              </div>

              {/* 요소 패널 리스트 */}
              <div className="space-y-4">
                {ELEMENT_TYPES.map(renderElementPanel)}
              </div>
            </div>
          )}

          {/* Generated Results Summary */}
          {analysis && Object.values(generatedImages).some(Boolean) && (
            <div className="mt-6 p-4 bg-bg-secondary rounded-lg border border-border-subtle">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">생성된 이미지</h3>
                <button
                  onClick={() => setShowSavePrompt(true)}
                  className="btn btn-secondary text-sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  프롬프트 저장
                </button>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                {ELEMENT_TYPES.map((element) => {
                  const generated = generatedImages[element]
                  const config = elementConfig[element]
                  const Icon = config.icon
                  return (
                    <div key={element} className="text-center">
                      <div className="aspect-[3/4] bg-bg-elevated rounded-lg overflow-hidden mb-2">
                        {generated ? (
                          <img src={generated} alt={config.label} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon className="w-8 h-8 text-text-tertiary" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-text-secondary">{config.label}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Integrated Cover Result */}
          {analysis?.integrated_cover_asset_id && (
            <div className="mt-6 p-6 bg-gradient-to-br from-accent-primary/10 to-accent-gold/10 rounded-lg border-2 border-accent-primary/30">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-accent-gold" />
                <h3 className="font-bold text-lg">통합 표지 이미지</h3>
                <span className="text-xs text-text-tertiary">
                  ({analysis.integrated_cover_model} · {new Date(analysis.integrated_cover_generated_at || '').toLocaleString('ko-KR')})
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="aspect-[3/4] bg-bg-elevated rounded-lg overflow-hidden">
                  <img
                    src={`${API_BASE}/assets/${analysis.integrated_cover_asset_id}/image`}
                    alt="통합 표지"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-text-secondary mb-1">사용된 요소</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.integrated_cover_elements && JSON.parse(analysis.integrated_cover_elements as string).map((elem: string) => (
                        <span key={elem} className="px-2 py-1 bg-accent-primary/20 text-accent-primary text-xs rounded">
                          {elem}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-secondary mb-1">생성 프롬프트</p>
                    <p className="text-xs text-text-tertiary bg-bg-elevated p-3 rounded max-h-40 overflow-y-auto">
                      {analysis.integrated_cover_prompt}
                    </p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => window.open(`/assets?highlight=${analysis.integrated_cover_asset_id}`, '_blank')}
                      className="flex-1 px-4 py-2 bg-accent-primary hover:bg-accent-primary/90 text-white text-sm rounded-lg transition-colors"
                    >
                      Assets에서 보기
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowHistory(false)} />
          <div className="relative bg-bg-secondary rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <h2 className="text-xl font-bold">분석 이력</h2>
              <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-bg-elevated rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* 필터링 옵션 */}
            <div className="px-4 pt-3 pb-2 border-b border-border-subtle flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={historyFilterByAsset}
                  onChange={(e) => {
                    setHistoryFilterByAsset(e.target.checked)
                    loadHistory(e.target.checked)
                  }}
                  disabled={!sourceAsset}
                  className="w-4 h-4 rounded border-border-default text-accent-gold focus:ring-accent-gold"
                />
                <span className={clsx('text-sm', !sourceAsset && 'text-text-tertiary')}>
                  현재 이미지만
                </span>
              </label>
              <span className="text-xs text-text-tertiary">
                {historyFilterByAsset && sourceAsset ? `(${sourceAsset.name || '선택된 이미지'})` : '최근 모든 분석'}
              </span>
            </div>
            <div className="p-4 overflow-y-auto max-h-[55vh]">
              {analysisHistory.length === 0 ? (
                <p className="text-center text-text-tertiary py-8">
                  {historyFilterByAsset ? '이 이미지의 분석 이력이 없습니다' : '분석 이력이 없습니다'}
                </p>
              ) : (
                <div className="space-y-3">
                  {analysisHistory.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 p-3 bg-bg-elevated rounded-lg hover:bg-bg-primary transition-colors cursor-pointer"
                      onClick={() => {
                        setAnalysis(item)
                        setIsLoadedFromHistory(true)
                        setCustomPrompts({
                          background: item.background_prompt || '',
                          character: item.character_prompt || '',
                          title: item.typography_prompt || '',
                          subtitle: item.subtitle_prompt || '',
                          author: item.author_prompt || '',
                          publisher: item.publisher_prompt || '',
                        })
                        if (item.source_image_url) {
                          setSourceImage(item.source_image_url)
                        }
                        setShowHistory(false)
                      }}
                    >
                      <div className="w-12 h-16 bg-bg-secondary rounded overflow-hidden flex-shrink-0">
                        {item.source_image_url ? (
                          <img src={item.source_image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-full h-full p-3 text-text-tertiary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          분석 #{item.id.slice(-8)}
                        </p>
                        <p className="text-xs text-text-tertiary">
                          {new Date(item.created_at).toLocaleString('ko-KR')}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">
                          상태: {item.status === 'completed' ? '완료' : item.status}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-text-tertiary flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save Prompt Modal */}
      {showSavePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowSavePrompt(false)} />
          <div className="relative bg-bg-secondary rounded-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <h2 className="text-xl font-bold">프롬프트 저장</h2>
              <button onClick={() => setShowSavePrompt(false)} className="p-2 hover:bg-bg-elevated rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">이름</label>
                <input
                  type="text"
                  value={savePromptName}
                  onChange={(e) => setSavePromptName(e.target.value)}
                  placeholder="나만의 프롬프트 이름"
                  className="input w-full"
                />
              </div>

              <div className="bg-bg-elevated rounded-lg p-3 text-sm">
                <h4 className="font-medium mb-2">저장될 프롬프트</h4>
                <div className="space-y-1 text-text-secondary text-xs">
                  {ELEMENT_TYPES.map((el) => (
                    <p key={el} className="truncate">
                      <span className="text-text-tertiary">{elementConfig[el].label}:</span>{' '}
                      {customPrompts[el] ? `${customPrompts[el].slice(0, 50)}...` : '(없음)'}
                    </p>
                  ))}
                </div>
              </div>

              <button
                onClick={async () => {
                  if (!savePromptName.trim()) return
                  try {
                    await analyzerApi.savePrompt({
                      name: savePromptName,
                      analysisId: analysis?.id,
                      prompts: customPrompts,
                      variations: { ...variations, global: globalVariation },
                    })
                    setShowSavePrompt(false)
                    setSavePromptName('')
                  } catch (err) {
                    console.error('Failed to save prompt:', err)
                  }
                }}
                disabled={!savePromptName.trim()}
                className="btn btn-primary w-full"
              >
                <Save className="w-4 h-4 mr-2" />
                저장하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Editor Modal - 세부요소별 편집 */}
      {showDetailEditor && detailEditorElement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => {
            setShowDetailEditor(false)
            setShowModelSelector(false)
          }} />
          <div className="relative bg-bg-secondary rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <div className="flex items-center gap-3">
                <div className={clsx('p-2 rounded-lg', elementConfig[detailEditorElement].color, 'bg-opacity-20')}>
                  {(() => {
                    const Icon = elementConfig[detailEditorElement].icon
                    return <Icon className={clsx('w-5 h-5', elementConfig[detailEditorElement].color.replace('bg-', 'text-'))} />
                  })()}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{elementConfig[detailEditorElement].label} 상세 편집</h2>
                  <p className="text-xs text-text-tertiary">세부요소별 변경율을 조절하고 AI로 텍스트를 변형하세요</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* AI 모델 선택 드롭다운 */}
                <div className="relative">
                  <button
                    onClick={() => setShowModelSelector(!showModelSelector)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-bg-elevated hover:bg-bg-primary transition-colors text-sm"
                  >
                    <Sparkles className="w-4 h-4 text-accent-gold" />
                    <span className="font-medium">
                      {availableTransformModels.find(m => m.id === transformAiModel)?.name || '모델 선택'}
                    </span>
                    <ChevronRight className={clsx(
                      'w-4 h-4 transition-transform',
                      showModelSelector && 'rotate-90'
                    )} />
                  </button>

                  {showModelSelector && (
                    <div className="absolute top-full mt-1 right-0 z-10 bg-bg-secondary border border-border-subtle rounded-lg shadow-lg overflow-hidden min-w-[300px] max-h-[400px] overflow-y-auto">
                      {/* Claude 모델들 */}
                      <div className="p-2 border-b border-border-subtle bg-bg-elevated">
                        <p className="text-xs font-semibold text-text-secondary">Claude 모델</p>
                      </div>
                      {availableTransformModels
                        .filter(m => m.provider === 'claude')
                        .map((model) => (
                          <button
                            key={model.id}
                            onClick={() => {
                              setTransformAiModel(model.id)
                              setShowModelSelector(false)
                            }}
                            className={clsx(
                              'w-full text-left px-3 py-2 hover:bg-bg-elevated transition-colors',
                              transformAiModel === model.id && 'bg-accent-gold/10'
                            )}
                          >
                            <p className="text-sm font-medium">{model.name}</p>
                            <p className="text-xs text-text-tertiary">{model.description}</p>
                          </button>
                        ))}

                      {/* Gemini 모델들 */}
                      <div className="p-2 border-b border-border-subtle bg-bg-elevated mt-1">
                        <p className="text-xs font-semibold text-text-secondary">Gemini 모델</p>
                      </div>
                      {availableTransformModels
                        .filter(m => m.provider === 'gemini')
                        .map((model) => (
                          <button
                            key={model.id}
                            onClick={() => {
                              setTransformAiModel(model.id)
                              setShowModelSelector(false)
                            }}
                            className={clsx(
                              'w-full text-left px-3 py-2 hover:bg-bg-elevated transition-colors',
                              transformAiModel === model.id && 'bg-accent-gold/10'
                            )}
                          >
                            <p className="text-sm font-medium">{model.name}</p>
                            <p className="text-xs text-text-tertiary">{model.description}</p>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDetailEditor(false)} className="p-2 hover:bg-bg-elevated rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 p-4 overflow-y-auto space-y-4">
              {Object.keys(detailTexts).map((field) => (
                <div key={field} className="bg-bg-elevated rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold">{detailFieldConfig[field]?.label || field}</label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-tertiary">변경율:</span>
                      <span className="text-sm font-bold text-accent-gold">{detailVariations[field] || 0}%</span>
                    </div>
                  </div>

                  {/* Variation Slider */}
                  <div className="mb-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={detailVariations[field] || 0}
                      onChange={(e) => setDetailVariations(prev => ({ ...prev, [field]: parseInt(e.target.value) }))}
                      className="w-full accent-accent-gold"
                    />
                    <div className="flex justify-between text-xs text-text-tertiary mt-1">
                      <span>원본 유지</span>
                      <span>크게 변형</span>
                    </div>
                  </div>

                  {/* Text Editor */}
                  <div className="relative">
                    <textarea
                      value={detailTexts[field] || ''}
                      onChange={(e) => setDetailTexts(prev => ({ ...prev, [field]: e.target.value }))}
                      placeholder={detailFieldConfig[field]?.placeholder || '내용을 입력하세요...'}
                      className="input w-full min-h-[80px] resize-y pr-24"
                    />
                    {/* AI Transform Button */}
                    <button
                      onClick={() => transformDetailText(field)}
                      disabled={isTransforming[field] || (detailVariations[field] || 0) === 0}
                      className={clsx(
                        'absolute right-2 top-2 flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
                        (detailVariations[field] || 0) > 0
                          ? 'bg-accent-gold/20 text-accent-gold hover:bg-accent-gold/30'
                          : 'bg-bg-secondary text-text-tertiary cursor-not-allowed'
                      )}
                    >
                      {isTransforming[field] ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          변형 중...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" />
                          AI 변형
                        </>
                      )}
                    </button>
                  </div>

                  {/* Variation Level Description */}
                  {(detailVariations[field] || 0) > 0 && (
                    <p className="text-xs text-text-tertiary mt-2">
                      {(detailVariations[field] || 0) <= 25 && '약간의 변화: 기존 의미를 유지하면서 표현을 다듬습니다'}
                      {(detailVariations[field] || 0) > 25 && (detailVariations[field] || 0) <= 50 && '중간 변화: 유사한 콘셉트 내에서 새로운 요소를 추가합니다'}
                      {(detailVariations[field] || 0) > 50 && (detailVariations[field] || 0) <= 75 && '큰 변화: 새로운 스타일이나 방향으로 재해석합니다'}
                      {(detailVariations[field] || 0) > 75 && '완전히 새로운: 기존 요소를 바탕으로 완전히 새롭게 창작합니다'}
                    </p>
                  )}
                </div>
              ))}

              {Object.keys(detailTexts).length === 0 && (
                <div className="text-center py-8 text-text-tertiary">
                  <p>편집 가능한 세부요소가 없습니다.</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-border-subtle flex items-center justify-between">
              <button
                onClick={() => {
                  // 모든 필드 한번에 AI 변형
                  Object.keys(detailTexts).forEach(field => {
                    if ((detailVariations[field] || 0) > 0) {
                      transformDetailText(field)
                    }
                  })
                }}
                disabled={Object.values(isTransforming).some(Boolean) || Object.values(detailVariations).every(v => v === 0)}
                className="btn btn-secondary"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                전체 AI 변형
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowDetailEditor(false)}
                  className="btn btn-secondary"
                >
                  취소
                </button>
                <button
                  onClick={applyDetailEdits}
                  className="btn btn-primary"
                >
                  <Check className="w-4 h-4 mr-2" />
                  적용하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Manager Modal */}
      {showTemplateManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowTemplateManager(false)} />
          <div className="relative bg-bg-secondary rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <h2 className="text-xl font-bold">추출용 프롬프트 관리</h2>
              <button onClick={() => setShowTemplateManager(false)} className="p-2 hover:bg-bg-elevated rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex">
              {/* 템플릿 목록 */}
              <div className="w-1/3 border-r border-border-subtle p-4 overflow-y-auto">
                <h3 className="text-sm font-semibold text-text-secondary mb-3">템플릿 목록</h3>
                <div className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={clsx(
                        'p-3 rounded-lg cursor-pointer transition-colors',
                        selectedTemplate === template.id
                          ? 'bg-accent-gold/10 border border-accent-gold'
                          : 'bg-bg-elevated hover:bg-bg-primary border border-transparent'
                      )}
                      onClick={() => setSelectedTemplate(template.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{template.name}</p>
                          <p className="text-xs text-text-tertiary">
                            {template.is_system ? '시스템' : '나의 템플릿'} · {template.element_type}
                          </p>
                        </div>
                        {!template.is_system && (
                          <div className="flex items-center gap-1 ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                startEditTemplate(template)
                              }}
                              className="p-1 hover:bg-bg-secondary rounded"
                              title="수정"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteTemplate(template.id)
                              }}
                              className="p-1 hover:bg-red-500/20 rounded text-red-400"
                              title="삭제"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 템플릿 편집/생성 */}
              <div className="flex-1 p-4 overflow-y-auto">
                <h3 className="text-sm font-semibold text-text-secondary mb-3">
                  {editingTemplate ? '템플릿 수정' : '새 템플릿 만들기'}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">템플릿 이름</label>
                    <input
                      type="text"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder="예: 웹소설 표지 분석용"
                      className="input w-full"
                    />
                  </div>

                  {!editingTemplate && (
                    <div>
                      <label className="block text-sm font-medium mb-2">적용 대상</label>
                      <select
                        value={newTemplateType}
                        onChange={(e) => setNewTemplateType(e.target.value as typeof newTemplateType)}
                        className="input w-full"
                      >
                        <option value="all">전체 요소</option>
                        <option value="background">배경</option>
                        <option value="character">인물</option>
                        <option value="typography">타이포그래피</option>
                        <option value="effect">효과</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-2">추출용 프롬프트</label>
                    <textarea
                      value={newTemplateText}
                      onChange={(e) => setNewTemplateText(e.target.value)}
                      placeholder="이미지에서 추출할 내용을 지시하는 프롬프트를 입력하세요...

예시:
이 웹소설 표지 이미지를 분석해주세요.
다음 요소들을 추출해주세요:
1. 배경: 장면, 분위기, 색상
2. 인물: 외모, 의상, 포즈, 표정
3. 제목: 폰트 스타일, 배치, 효과
4. 부제목: 폰트, 위치
5. 저자명: 폰트, 위치
6. 출판사: 로고, 위치

각 요소에 대해 재생성용 프롬프트도 제안해주세요."
                      className="input w-full min-h-[200px] resize-y font-mono text-sm"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    {editingTemplate && (
                      <button
                        onClick={cancelEditTemplate}
                        className="btn btn-secondary flex-1"
                      >
                        취소
                      </button>
                    )}
                    <button
                      onClick={handleSaveTemplate}
                      disabled={!newTemplateName.trim() || !newTemplateText.trim() || templateSaving}
                      className="btn btn-primary flex-1"
                    >
                      {templateSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          저장 중...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {editingTemplate ? '수정 저장' : '새 템플릿 저장'}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* 선택된 템플릿 미리보기 */}
                {selectedTemplate && templates.find(t => t.id === selectedTemplate) && (
                  <div className="mt-6 pt-4 border-t border-border-subtle">
                    <h4 className="text-sm font-semibold text-text-secondary mb-2">
                      선택된 템플릿 미리보기
                    </h4>
                    <div className="bg-bg-elevated rounded-lg p-3">
                      <p className="font-medium text-sm mb-2">
                        {templates.find(t => t.id === selectedTemplate)?.name}
                      </p>
                      <pre className="text-xs text-text-secondary whitespace-pre-wrap font-mono max-h-[200px] overflow-y-auto">
                        {templates.find(t => t.id === selectedTemplate)?.template_text || '(내용 없음)'}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integrated Cover Generation Modal */}
      {showIntegratedCoverModal && analysis && (
        <IntegratedCoverGenerationModal
          analysis={analysis}
          userPoints={points}
          onClose={() => setShowIntegratedCoverModal(false)}
          onSuccess={async (assetId) => {
            console.log('Integrated cover generated:', assetId)

            // 분석 정보 다시 로드하여 통합 표지 정보 반영
            if (analysis) {
              const result = await analyzerApi.getAnalysis(analysis.id)
              if (result.success && result.data) {
                setAnalysis(result.data)
                console.log('Analysis reloaded with integrated cover info')
              }
            }

            // 모달은 자동으로 닫지 않고 사용자가 "Assets에서 보기" 또는 "닫기" 버튼 선택
            // setShowIntegratedCoverModal(false)
          }}
        />
      )}

      {/* Image Viewer Modal */}
      {showImageViewer && sourceImage && (
        <ImageViewerModal
          imageUrl={sourceImage}
          imageName={sourceAsset?.name || uploadedFile?.name || '원본 이미지'}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      {/* Reanalyze Modal */}
      {reanalyzeElement && analysis && (
        <ReanalyzeModal
          analysisId={analysis.id}
          elementType={reanalyzeElement}
          elementLabel={elementConfig[reanalyzeElement].label}
          onClose={() => setReanalyzeElement(null)}
          onSuccess={async () => {
            // Reload analysis
            const result = await analyzerApi.getAnalysis(analysis.id)
            if (result.success && result.data) {
              setAnalysis(result.data)

              // Update prompts
              setOriginalPrompts({
                background: getEffectivePrompt('background', {
                  generated: result.data.background_prompt,
                  analysis: result.data.background_analysis
                }),
                character: getEffectivePrompt('character', {
                  generated: result.data.character_prompt,
                  analysis: result.data.character_analysis
                }),
                title: getEffectivePrompt('title', {
                  generated: result.data.typography_prompt,
                  analysis: result.data.typography_analysis
                }),
                subtitle: getEffectivePrompt('subtitle', {
                  generated: result.data.subtitle_prompt,
                  analysis: result.data.subtitle_analysis
                }),
                author: getEffectivePrompt('author', {
                  generated: result.data.author_prompt,
                  analysis: result.data.author_analysis
                }),
                publisher: getEffectivePrompt('publisher', {
                  generated: result.data.publisher_prompt,
                  analysis: result.data.publisher_analysis
                }),
              })
            }
            setReanalyzeElement(null)
          }}
        />
      )}

      {/* Analysis History Modal */}
      {showHistoryModal && (
        <AnalysisHistoryModal
          onClose={() => setShowHistoryModal(false)}
          onSelect={async (selectedAnalysis) => {
            // Load the selected analysis
            setAnalysis(selectedAnalysis)
            setIsLoadedFromHistory(true)

            // Set source image
            if (selectedAnalysis.source_image_url) {
              setSourceImage(selectedAnalysis.source_image_url)
            }

            // Set source asset if available
            if (selectedAnalysis.asset_id) {
              const assetResult = await assetsApi.get(selectedAnalysis.asset_id)
              if (assetResult.success && assetResult.data) {
                setSourceAsset(assetResult.data)
              }
            } else {
              setSourceAsset(null)
            }

            // Update prompts
            setOriginalPrompts({
              background: getEffectivePrompt('background', {
                generated: selectedAnalysis.background_prompt,
                analysis: selectedAnalysis.background_analysis
              }),
              character: getEffectivePrompt('character', {
                generated: selectedAnalysis.character_prompt,
                analysis: selectedAnalysis.character_analysis
              }),
              title: getEffectivePrompt('title', {
                generated: selectedAnalysis.typography_prompt,
                analysis: selectedAnalysis.typography_analysis
              }),
              subtitle: getEffectivePrompt('subtitle', {
                generated: selectedAnalysis.subtitle_prompt,
                analysis: selectedAnalysis.subtitle_analysis
              }),
              author: getEffectivePrompt('author', {
                generated: selectedAnalysis.author_prompt,
                analysis: selectedAnalysis.author_analysis
              }),
              publisher: getEffectivePrompt('publisher', {
                generated: selectedAnalysis.publisher_prompt,
                analysis: selectedAnalysis.publisher_analysis
              }),
            })

            setCustomPrompts({
              background: getEffectivePrompt('background', {
                generated: selectedAnalysis.background_prompt,
                analysis: selectedAnalysis.background_analysis
              }),
              character: getEffectivePrompt('character', {
                generated: selectedAnalysis.character_prompt,
                analysis: selectedAnalysis.character_analysis
              }),
              title: getEffectivePrompt('title', {
                generated: selectedAnalysis.typography_prompt,
                analysis: selectedAnalysis.typography_analysis
              }),
              subtitle: getEffectivePrompt('subtitle', {
                generated: selectedAnalysis.subtitle_prompt,
                analysis: selectedAnalysis.subtitle_analysis
              }),
              author: getEffectivePrompt('author', {
                generated: selectedAnalysis.author_prompt,
                analysis: selectedAnalysis.author_analysis
              }),
              publisher: getEffectivePrompt('publisher', {
                generated: selectedAnalysis.publisher_prompt,
                analysis: selectedAnalysis.publisher_analysis
              }),
            })
          }}
        />
      )}

      {/* Asset 등록 확인 다이얼로그 */}
      {registerAssetDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-primary rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Asset 등록 확인</h2>
                <button
                  onClick={() => setRegisterAssetDialog(null)}
                  className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* 미리보기 이미지 */}
                <div>
                  <label className="block text-sm font-medium mb-2">미리보기</label>
                  <div className="aspect-video bg-bg-elevated rounded-lg overflow-hidden flex items-center justify-center">
                    <img
                      src={registerAssetDialog.imageUrl}
                      alt="Preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </div>

                {/* 제목 */}
                <div>
                  <label className="block text-sm font-medium mb-2">제목 *</label>
                  <input
                    type="text"
                    value={registerAssetDialog.metadata.name}
                    onChange={(e) => setRegisterAssetDialog({
                      ...registerAssetDialog,
                      metadata: { ...registerAssetDialog.metadata, name: e.target.value }
                    })}
                    className="input w-full"
                    placeholder="Asset 제목을 입력하세요"
                  />
                </div>

                {/* 설명 */}
                <div>
                  <label className="block text-sm font-medium mb-2">설명</label>
                  <textarea
                    value={registerAssetDialog.metadata.description}
                    onChange={(e) => setRegisterAssetDialog({
                      ...registerAssetDialog,
                      metadata: { ...registerAssetDialog.metadata, description: e.target.value }
                    })}
                    className="input w-full min-h-[100px]"
                    placeholder="Asset 설명을 입력하세요"
                  />
                </div>

                {/* 태그 */}
                <div>
                  <label className="block text-sm font-medium mb-2">태그 (자동 추출)</label>
                  <div className="flex flex-wrap gap-2 p-3 bg-bg-elevated rounded-lg min-h-[60px]">
                    {registerAssetDialog.metadata.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-accent-gold/10 text-accent-gold rounded-full text-sm"
                      >
                        {tag}
                        <button
                          onClick={() => {
                            const newTags = registerAssetDialog.metadata.tags.filter((_, i) => i !== index)
                            setRegisterAssetDialog({
                              ...registerAssetDialog,
                              metadata: { ...registerAssetDialog.metadata, tags: newTags }
                            })
                          }}
                          className="hover:bg-accent-gold/20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-text-tertiary mt-2">
                    프롬프트에서 자동 추출된 키워드입니다. X 버튼으로 제거할 수 있습니다.
                  </p>
                </div>

                {/* 프롬프트 (읽기 전용) */}
                <div>
                  <label className="block text-sm font-medium mb-2">프롬프트 (참고용)</label>
                  <div className="p-3 bg-bg-elevated rounded-lg text-sm text-text-secondary">
                    {registerAssetDialog.prompt}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setRegisterAssetDialog(null)}
                  className="btn btn-secondary flex-1"
                  disabled={isAnalyzing}
                >
                  취소
                </button>
                <button
                  onClick={handleConfirmRegisterAsset}
                  className="btn btn-primary flex-1"
                  disabled={isAnalyzing || !registerAssetDialog.metadata.name.trim()}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      등록 중...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Asset 등록
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
